/**
 * 微信小程序 web-view 环境工具
 * 判定：UA 含 miniProgram（与 isApp 并存，小程序 token 入场也会把 isApp 置 true）
 */
import { buildHostContext, normalizeChannelNumber } from '@/utils/host'

/** 旧版告警/设备共用脏标记，读取时兼容一次 */
const LEGACY_LIST_DIRTY_KEY = '__ALARM_MP_LIST_DIRTY__'

const LIST_DIRTY_KEYS = {
  alarm: '__MP_LIST_DIRTY_ALARM__',
  sensor: '__MP_LIST_DIRTY_SENSOR__',
}

function listDirtyKey(scope) {
  return LIST_DIRTY_KEYS[scope] || LIST_DIRTY_KEYS.alarm
}

/** 小程序内告警详情页路径（需与小程序工程一致） */
export const MP_ALARM_DETAIL_PATH = '/pages/web/detail'

/** 小程序内设备详情页路径（需与小程序工程一致） */
export const MP_SENSOR_DETAIL_PATH = '/pages/web/sensor-detail'

/** 小程序内时序图全屏页路径（需与小程序工程一致） */
export const MP_SENSOR_CHART_FULLSCREEN_PATH = '/pages/web/sensor-chart-fullscreen'

/** 是否运行在小程序 web-view 中 */
export function isMiniProgram() {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /miniProgram/i.test(ua) || window.__wxjs_environment === 'miniprogram'
}

function getWxMiniProgram() {
  return window.wx?.miniProgram || null
}

/**
 * 跳转小程序原生告警详情页（另一 web-view 承载 alarm/detail）
 * @param {object} row 告警行
 * @param {string} [path] 小程序页面路径，默认 MP_ALARM_DETAIL_PATH
 */
export function navigateToMiniProgramAlarmDetail(row, path = MP_ALARM_DETAIL_PATH) {
  const mp = getWxMiniProgram()
  if (!mp?.navigateTo || !row) return false
  const query = new URLSearchParams(buildHostContext({ id: String(row.id) }))
  mp.navigateTo({ url: `${path}?${query.toString()}` })
  return true
}

/**
 * 跳转小程序原生设备详情页（另一 web-view 承载 sensor/detail）
 * @param {object} row 设备行
 * @param {string} [path] 小程序页面路径，默认 MP_SENSOR_DETAIL_PATH
 */
export function navigateToMiniProgramSensorDetail(
  row,
  path = MP_SENSOR_DETAIL_PATH
) {
  const mp = getWxMiniProgram()
  if (!mp?.navigateTo || !row?.sn) return false
  const query = new URLSearchParams(
    buildHostContext({
      sn: String(row.sn),
      channel_number: String(normalizeChannelNumber(row.channel_number)),
    })
  )
  mp.navigateTo({ url: `${path}?${query.toString()}` })
  return true
}

/**
 * 跳转小程序原生时序图全屏页（另一 web-view）
 * @param {Record<string, string>} chartQuery buildSensorChartFullscreenQuery 结果
 * @param {string} [path]
 */
export function navigateToMiniProgramSensorChartFullscreen(
  chartQuery,
  path = MP_SENSOR_CHART_FULLSCREEN_PATH
) {
  const mp = getWxMiniProgram()
  const projectId = window.$store?.currentProject?.id
  if (!mp?.navigateTo || !chartQuery?.sn || !chartQuery?.fieldKey || !projectId) {
    return false
  }
  const ctx = buildHostContext()
  const query = new URLSearchParams({
    app_token: ctx.app_token,
    project_id: ctx.project_id,
    ...chartQuery,
  })
  mp.navigateTo({ url: `${path}?${query.toString()}` })
  return true
}

/** 小程序返回上一页 */
export function navigateBackMiniProgram(delta = 1) {
  const mp = getWxMiniProgram()
  if (!mp?.navigateBack) return false
  mp.navigateBack({ delta })
  return true
}

/**
 * 详情内操作成功后标记列表需刷新（同域 localStorage，跨 web-view 可读）
 * @param {'alarm' | 'sensor'} [scope]
 */
export function markMiniProgramListDirty(scope = 'alarm') {
  try {
    localStorage.setItem(listDirtyKey(scope), '1')
  } catch {
    // ignore
  }
}

/** 消费脏标记；有脏则返回 true 并清除。读到旧共用 key 时也算一次 */
export function consumeMiniProgramListDirty(scope = 'alarm') {
  try {
    const key = listDirtyKey(scope)
    if (localStorage.getItem(key) === '1') {
      localStorage.removeItem(key)
      return true
    }
    if (localStorage.getItem(LEGACY_LIST_DIRTY_KEY) === '1') {
      localStorage.removeItem(LEGACY_LIST_DIRTY_KEY)
      return true
    }
    return false
  } catch {
    return false
  }
}

/**
 * 列表页：从详情 navigateBack 后页面重新可见时，若有脏标记则回调刷新
 * @param {() => void} onRefresh
 * @param {'alarm' | 'sensor'} [scope]
 * @returns {() => void} 取消监听
 */
export function setupMiniProgramListRefresh(onRefresh, scope = 'alarm') {
  if (!isMiniProgram() || typeof onRefresh !== 'function') return () => {}

  const tryRefresh = () => {
    if (document.visibilityState !== 'visible') return
    if (consumeMiniProgramListDirty(scope)) onRefresh()
  }

  const onPageShow = (e) => {
    // bfcache 恢复时也检查
    if (e?.persisted || document.visibilityState === 'visible') tryRefresh()
  }

  document.addEventListener('visibilitychange', tryRefresh)
  window.addEventListener('pageshow', onPageShow)

  return () => {
    document.removeEventListener('visibilitychange', tryRefresh)
    window.removeEventListener('pageshow', onPageShow)
  }
}
